/**
 * BR-10 `/submissions` and BR-11 `/submissions/:id`.
 *
 * Two things the SRS singles out for these screens, both visible here.
 *
 * **Reviews are limited to the brand's own campaigns.** Not by a filter on
 * this page - the organization is resolved from the session and verified
 * inside `sub.review_submission`, and another brand's submission comes
 * back absent rather than forbidden. The screen could not reach one if it
 * tried.
 *
 * **The moderation hold remains independent.** A submission under a
 * platform hold is still reviewable, and approving it is still the right
 * thing for a brand to do if the work is good. The hold is shown so nobody
 * is surprised when an approved post does not pay yet, and the copy says
 * plainly that clearing it is not the brand's to do.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Badge, Button, Card, Eyebrow, PageHeader, StateBlock, Money,
} from "@ui/primitives";
import { Select, TextInput, FormError } from "@ui/forms";
import { fontSize, space } from "@design/index";
import { fromMinorString } from "@contracts/money";
import { api } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";
import { platformMeta, profileUrl } from "@app/shared/Platforms";
import { postOpenUrl } from "@server/platforms";
import { CreatorAvatar } from "@app/shared/icons";

interface Submission {
  id: string; campaign_id: string; campaign_name: string | null;
  creator_id?: string | null;
  creator_name: string | null; creator_handle?: string | null;
  avatar_href?: string | null;
  platform: string; post_id: string | null;
  submitted_url: string; canonical_url: string | null;
  state: string; moderation_state: string; availability: string;
  current_revision: number; submitted_at: string;
  decided_at: string | null; decision_note: string | null;
  caption?: string | null; notes?: string | null;
  earned_minor?: string | null;
}

interface Detail {
  submission: Submission;
  revisions: Array<{ revision: number; caption: string | null; notes: string | null; created_at: string }>;
  reviews: Array<{ revision: number; decision: string; reason: string | null; created_at: string }>;
}

const TONE: Record<string, "ok" | "warn" | "danger" | "process" | "neutral"> = {
  APPROVED: "ok", SUBMITTED: "process", PENDING_VERIFICATION: "process",
  REVISION_REQUESTED: "warn", REJECTED: "danger", WITHDRAWN: "neutral", REPLACED: "neutral",
};
const StateBadge = ({ value }: { value: string }) =>
  <Badge tone={TONE[value] ?? "neutral"}>{value.replaceAll("_", " ").toLowerCase()}</Badge>;

const QUEUES = [
  { value: "SUBMITTED", label: "Awaiting review" },
  { value: "REVISION_REQUESTED", label: "Revision requested" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "ALL", label: "Everything" },
];

const DECISIONS = [
  { value: "APPROVED", label: "Approve" },
  { value: "REVISION_REQUESTED", label: "Request a revision" },
  { value: "REJECTED", label: "Reject" },
];

export function ConnectedAccountLink({
  platform, handle,
}: { platform: string; handle?: string | null }) {
  const url = profileUrl(platform, handle);
  const name = platformMeta(platform).label;
  const label = handle
    ? (handle.startsWith("@") ? handle : `@${handle}`)
    : null;
  if (!url) {
    return (
      <span>
        Connected {name} account{label ? ` ${label}` : " (no username stored)"}
      </span>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer noopener"
      style={{ color: "var(--accent)", fontWeight: 650 }}>
      Open {name} profile{label ? ` · ${label}` : ""}
    </a>
  );
}

function EarnedAmount({ minor, currency }: { minor?: string | null; currency: string }) {
  const n = Number(minor ?? 0);
  if (!Number.isFinite(n) || n <= 0) {
    return <span style={{ color: "var(--faint)" }}>No earnings yet</span>;
  }
  return <Money value={fromMinorString(String(n), currency as "GBP" | "USD" | "EUR")} />;
}

/** How long this has been waiting, which is the queue's real ordering. */
function age(iso: string): string {
  const hours = Math.floor((Date.now() - Date.parse(iso)) / 3_600_000);
  if (!Number.isFinite(hours) || hours < 1) return "just now";
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/* --------------------------------------------------------------- BR-11 */

function ReviewDetail({ id, onBack, currency = "USD" }: {
  id: string; onBack: () => void; currency?: string;
}) {
  const toast = useToast();
  const [detail, setDetail] = useState<Detail | null | "error">(null);
  const [decision, setDecision] = useState("APPROVED");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await api.get<Detail>(`/submissions/${id}`);
    setDetail(r.ok ? r.data : "error");
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  async function decide() {
    setBusy(true); setError(null);
    const r = await api.post<{ decision: string }>(`/submissions/${id}/review`,
      { decision, reason });
    setBusy(false);
    if (r.ok) { toast.ok("Recorded."); setReason(""); void load(); }
    else { toast.error(r.message); setError(r.message); }
  }

  if (detail === null) return <StateBlock kind="loading" title="Loading the submission…" />;
  if (detail === "error") {
    return <StateBlock kind="error" title="Could not load that submission"
      action={<Button size="sm" onClick={onBack}>Back to the queue</Button>} />;
  }

  const s = detail.submission;
  const openUrl = postOpenUrl({
    platform: s.platform,
    postId: s.post_id,
    submittedUrl: s.submitted_url,
    canonicalUrl: s.canonical_url,
    handle: s.creator_handle,
  });
  const current = detail.revisions.find((r) => r.revision === s.current_revision);
  const reviewable = s.state === "SUBMITTED" || s.state === "REVISION_REQUESTED";
  const needsReason = decision !== "APPROVED";

  return (
    <div style={{ display: "grid", gap: space(4) }}>
      <div><Button size="sm" onClick={onBack}>← Back to the queue</Button></div>

      <PageHeader eyebrow={s.campaign_name ?? "Campaign"}
        title={s.creator_name ?? "Submission"}
        description="The published post, what the creator said about it, and every decision so far."
        actions={<StateBadge value={s.state} />} />

      {error ? <FormError message={error} /> : null}

      {s.moderation_state !== "CLEAR" ? (
        <Card padding={16} style={{ borderColor: "var(--warn-line)" }}>
          <Eyebrow>Platform hold</Eyebrow>
          <p style={{ margin: "6px 0 0", fontSize: fontSize.body, lineHeight: 1.6 }}>
            This post is <strong>{s.moderation_state.toLowerCase()}</strong> under platform
            policy. That is a separate decision from yours and only the platform can lift it -
            your approval is still worth recording, and it will hold. Nothing pays out while
            the hold stands.
          </p>
        </Card>
      ) : null}

      <Card padding={18} style={{ display: "grid", gap: space(2) }}>
        <Eyebrow>The post</Eyebrow>
        <p style={{ margin: 0, fontSize: fontSize.body }}>
          <a href={openUrl} target="_blank" rel="noreferrer noopener"
             style={{ color: "var(--accent)" }}>
            {openUrl}
          </a>
        </p>
        <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--text-2)", lineHeight: 1.55 }}>
          <ConnectedAccountLink platform={s.platform} handle={s.creator_handle} />
          . Compare that account with the link before you decide.
        </p>
        <p style={{ margin: 0, fontSize: fontSize.body }}>
          Posted earnings: <EarnedAmount minor={s.earned_minor} currency={currency} />
          {" "}- after counted views and a claim you approve on the Earnings tab.
        </p>
        <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)" }}>
          {s.platform} · revision {s.current_revision} · submitted {age(s.submitted_at)} ago ·
          availability {s.availability.replaceAll("_", " ").toLowerCase()}
        </p>
        {current?.caption ? (
          <p style={{ margin: 0, fontSize: fontSize.body, lineHeight: 1.6 }}>
            <strong>Caption.</strong> {current.caption}
          </p>
        ) : null}
        {current?.notes ? (
          <p style={{ margin: 0, fontSize: fontSize.body, lineHeight: 1.6 }}>
            <strong>Notes.</strong> {current.notes}
          </p>
        ) : null}
      </Card>

      {detail.revisions.length > 1 ? (
        <Card padding={18} style={{ display: "grid", gap: space(2) }}>
          <Eyebrow>Revision history</Eyebrow>
          {detail.revisions.map((rv) => (
            <div key={rv.revision} style={{ display: "grid", gap: 2 }}>
              <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
                revision {rv.revision} · {new Date(rv.created_at).toISOString().slice(0, 10)}
              </span>
              <span style={{ fontSize: fontSize.label }}>{rv.caption ?? "-"}</span>
            </div>
          ))}
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)" }}>
            Revisions are corrections to the same post. The measurement identity does not change.
          </p>
        </Card>
      ) : null}

      {detail.reviews.length > 0 ? (
        <Card padding={18} style={{ display: "grid", gap: space(2) }}>
          <Eyebrow>Decisions</Eyebrow>
          {detail.reviews.map((rv, i) => (
            <div key={i} style={{ display: "grid", gap: 4 }}>
              <div style={{ display: "flex", gap: space(2), alignItems: "center" }}>
                <StateBadge value={rv.decision} />
                <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
                  revision {rv.revision} · {new Date(rv.created_at).toISOString().slice(0, 10)}
                </span>
              </div>
              {rv.reason ? (
                <p style={{ margin: 0, fontSize: fontSize.label, lineHeight: 1.6 }}>{rv.reason}</p>
              ) : null}
            </div>
          ))}
        </Card>
      ) : null}

      <Card padding={18} style={{ display: "grid", gap: space(2), maxWidth: 620 }}>
        <Eyebrow>Your decision</Eyebrow>
        {reviewable ? (
          <>
            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)", lineHeight: 1.6 }}>
              This decides revision {s.current_revision}. If the creator revises, it comes back
              to this queue and your decision here is superseded by the next one.
            </p>
            <Select label="Decision" value={decision}
              onChange={(e) => setDecision(e.currentTarget.value)}>
              {DECISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </Select>
            <TextInput label="Reason" required={needsReason} value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              hint={needsReason
                ? "The creator sees this. Tie it to the accepted brief."
                : "Optional."} />
            <div>
              <Button variant="primary" size="sm"
                disabled={busy || (needsReason && !reason.trim())}
                onClick={() => void decide()}>
                {busy ? "Recording…" : "Record decision"}
              </Button>
            </div>
          </>
        ) : (
          <StateBlock kind="empty" title="Nothing to decide"
            detail={`This submission is ${s.state.replaceAll("_", " ").toLowerCase()}.`} />
        )}
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------- BR-10 */

export function BrandSubmissionsScreen({
  openId: openIdProp = null, onOpen, onClose, campaignId, embedded = false, currency = "USD",
}: {
  /** BR-11 is `/submissions/:id`, a URL a reviewer can be linked to. */
  openId?: string | null;
  onOpen?: (id: string) => void;
  onClose?: () => void;
  campaignId?: string;
  embedded?: boolean;
  currency?: string;
}) {
  const [state, setState] = useState(embedded ? "ALL" : "SUBMITTED");
  const [rows, setRows] = useState<Submission[] | null | "error">(null);
  const [localOpen, setLocalOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    const qs = new URLSearchParams({ state });
    if (campaignId) qs.set("campaign", campaignId);
    const r = await api.get<{ submissions: Submission[] }>(`/submissions?${qs.toString()}`);
    const list = r.ok ? r.data.submissions : "error";
    if (list !== "error" && campaignId && !/^[0-9a-f-]{36}$/i.test(campaignId)) {
      setRows(list.filter((s) => s.campaign_id === campaignId));
    } else {
      setRows(list);
    }
  }, [state, campaignId]);
  useEffect(() => { void load(); }, [load]);

  const openId = embedded ? localOpen : openIdProp;
  const handleOpen = embedded ? (id: string) => setLocalOpen(id) : onOpen;
  const handleClose = embedded ? () => { setLocalOpen(null); void load(); } : onClose;

  if (openId) {
    return <ReviewDetail id={openId} currency={currency}
      onBack={() => { handleClose?.(); }} />;
  }

  const earnedTotal = Array.isArray(rows)
    ? rows.reduce((n, s) => n + Number(s.earned_minor ?? 0), 0)
    : 0;

  return (
    <>
      {embedded ? (
        <PageHeader eyebrow="Submissions" title="Posts on this campaign"
          description="Every post from joined creators, with the connected account so you can verify the profile and the amount earned so far."
          actions={earnedTotal > 0
            ? <Money value={fromMinorString(String(earnedTotal), currency as "GBP" | "USD" | "EUR")} emphasis />
            : null} />
      ) : (
        <PageHeader eyebrow="Submissions" title="Review queue"
          description="Posts your creators have published, oldest first. Only your own campaigns appear here." />
      )}

      <Card padding={14} style={{ marginBottom: space(3), maxWidth: 320 }}>
        <Select label="Queue" value={state} onChange={(e) => setState(e.currentTarget.value)}>
          {QUEUES.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
        </Select>
      </Card>

      {rows === null ? <StateBlock kind="loading" title="Loading the queue…" />
        : rows === "error" ? <StateBlock kind="error" title="Could not load the queue" />
        : rows.length === 0
          ? <StateBlock kind="empty" title="Nothing in this queue" />
          : (
            <div style={{ display: "grid", gap: space(2) }}>
              {rows.map((s) => (
                <Card key={s.id} padding={16} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: space(3), flexWrap: "wrap",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <CreatorAvatar src={s.avatar_href} name={s.creator_name ?? undefined} size={40} />
                    <div style={{ display: "grid", gap: 4 }}>
                    <button type="button" onClick={() => handleOpen?.(s.id)} style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      color: "var(--accent)", font: "inherit", textAlign: "left",
                      fontSize: fontSize.bodyLg,
                    }}>{s.creator_name ?? "A creator"}</button>
                    <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
                      {s.campaign_name ?? "campaign"} · {s.platform}
                      {" "}· revision {s.current_revision}
                    </span>
                    <span style={{ fontSize: fontSize.label }}>
                      <ConnectedAccountLink platform={s.platform} handle={s.creator_handle} />
                    </span>
                    <span style={{ fontSize: fontSize.label, color: "var(--text-2)" }}>
                      Earned <EarnedAmount minor={s.earned_minor} currency={currency} />
                    </span>
                    {s.caption ? (
                      <span style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                        {s.caption.slice(0, 120)}{s.caption.length > 120 ? "…" : ""}
                      </span>
                    ) : null}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: space(2), alignItems: "center" }}>
                    <span style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                      waiting {age(s.submitted_at)}
                    </span>
                    {s.moderation_state !== "CLEAR"
                      ? <Badge tone="warn">platform {s.moderation_state.toLowerCase()}</Badge> : null}
                    <StateBadge value={s.state} />
                  </div>
                </Card>
              ))}
            </div>
          )}
    </>
  );
}
